import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet';
import { Loader2, MapPin, Search } from 'lucide-react';
import { Badge, Button, Input, Label, toast } from '@exiftrator/shared/components/ui';
import { useLocations } from '../../../contexts/LocationsContext';
import { searchLocationsNormalized } from '../../../services/nominatimService';
import { EmptyState } from '../../EmptyState';
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';
import SettingsEditorDialog from '../components/SettingsEditorDialog';
import SettingsEntityCard from '../components/SettingsEntityCard';
import 'leaflet/dist/leaflet.css';

import L from 'leaflet';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const DEFAULT_MAP_POSITION = [37.7749, -122.4194];
const EMPTY_FORM = {
  name: '',
  latitude: '',
  longitude: '',
  address: '',
};

function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(timeoutId);
  }, [delay, value]);

  return debouncedValue;
}

function LocationSettings() {
  const { locations, addLocation, updateLocation, deleteLocation } = useLocations();

  const [dialogMode, setDialogMode] = useState(null);
  const [activeLocationId, setActiveLocationId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [mapPosition, setMapPosition] = useState(DEFAULT_MAP_POSITION);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const debouncedQuery = useDebounce(searchQuery, 350);
  const isEditorOpen = dialogMode !== null;
  const isEditMode = dialogMode === 'edit';
  const activeLocation = useMemo(
    () => locations.find((location) => location.id === activeLocationId) || null,
    [activeLocationId, locations],
  );

  const parsedLatitude = Number.parseFloat(formData.latitude);
  const parsedLongitude = Number.parseFloat(formData.longitude);

  const hasCoordinates = Number.isFinite(parsedLatitude) && Number.isFinite(parsedLongitude);

  const resetEditorState = useCallback(() => {
    setDialogMode(null);
    setActiveLocationId(null);
    setFormData(EMPTY_FORM);
    setSearchQuery('');
    setSearchResults([]);
    setIsSearching(false);
    setMapPosition(DEFAULT_MAP_POSITION);
  }, []);

  const handleCreateNew = useCallback(() => {
    setDialogMode('create');
    setActiveLocationId(null);
    setFormData(EMPTY_FORM);
    setSearchQuery('');
    setSearchResults([]);
    setMapPosition(DEFAULT_MAP_POSITION);
  }, []);

  const handleEdit = useCallback((location) => {
    setDialogMode('edit');
    setActiveLocationId(location.id);
    setFormData({
      name: location.name,
      latitude: location.latitude.toFixed(7),
      longitude: location.longitude.toFixed(7),
      address: location.address || '',
    });
    setSearchQuery('');
    setSearchResults([]);
    setMapPosition([location.latitude, location.longitude]);
  }, []);

  const handleDeleteRequest = useCallback((location) => {
    setDeleteTarget(location);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (!deleteTarget) {
      return;
    }

    deleteLocation(deleteTarget.id);

    if (activeLocationId === deleteTarget.id) {
      resetEditorState();
    }

    setDeleteTarget(null);
  }, [activeLocationId, deleteLocation, deleteTarget, resetEditorState]);

  useEffect(() => {
    if (!isEditorOpen || isEditMode) {
      return;
    }

    if (!debouncedQuery || debouncedQuery.trim().length < 3) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    let cancelled = false;

    const loadResults = async () => {
      setIsSearching(true);
      try {
        const results = await searchLocationsNormalized(debouncedQuery, { limit: 5 });
        if (!cancelled) {
          setSearchResults(results);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Location search error:', error);
          setSearchResults([]);
        }
      } finally {
        if (!cancelled) {
          setIsSearching(false);
        }
      }
    };

    loadResults();

    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, isEditorOpen, isEditMode]);

  const handleSelectSearchResult = useCallback((result) => {
    setFormData((previous) => ({
      ...previous,
      name: previous.name || result.primaryName || result.name.split(',')[0]?.trim() || '',
      latitude: result.latitude.toFixed(7),
      longitude: result.longitude.toFixed(7),
      address: result.address || result.name,
    }));

    setSearchQuery('');
    setSearchResults([]);
    setMapPosition([result.latitude, result.longitude]);
  }, []);

  const handleMapClick = useCallback((event) => {
    const { lat, lng } = event.latlng;

    setFormData((previous) => ({
      ...previous,
      latitude: lat.toFixed(7),
      longitude: lng.toFixed(7),
    }));
    setMapPosition([lat, lng]);
  }, []);

  const handleSubmit = useCallback(() => {
    const trimmedName = formData.name.trim();

    if (!trimmedName) {
      toast.error('Please enter a location name.');
      return;
    }

    if (isEditMode && activeLocationId !== null) {
      updateLocation(activeLocationId, { name: trimmedName });
      toast.success('Location name updated.');
      resetEditorState();
      return;
    }

    if (!Number.isFinite(parsedLatitude) || parsedLatitude < -90 || parsedLatitude > 90) {
      toast.error('Please enter a valid latitude between -90 and 90.');
      return;
    }

    if (!Number.isFinite(parsedLongitude) || parsedLongitude < -180 || parsedLongitude > 180) {
      toast.error('Please enter a valid longitude between -180 and 180.');
      return;
    }

    const payload = {
      name: trimmedName,
      latitude: Math.round(parsedLatitude * 10_000_000) / 10_000_000,
      longitude: Math.round(parsedLongitude * 10_000_000) / 10_000_000,
      address: formData.address.trim() || null,
    };

    const result = addLocation(payload.name, payload.latitude, payload.longitude, payload.address);
    if (result?.error) {
      toast.error(result.error);
      return;
    }
    toast.success('Location created.');

    resetEditorState();
  }, [
    activeLocationId,
    addLocation,
    formData.address,
    formData.name,
    isEditMode,
    parsedLatitude,
    parsedLongitude,
    resetEditorState,
    updateLocation,
  ]);

  const isFormValid = isEditMode
    ? formData.name.trim().length > 0
    : formData.name.trim().length > 0 &&
      Number.isFinite(parsedLatitude) &&
      parsedLatitude >= -90 &&
      parsedLatitude <= 90 &&
      Number.isFinite(parsedLongitude) &&
      parsedLongitude >= -180 &&
      parsedLongitude <= 180;

  const editorFooter = (
    <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
      <Button type="button" variant="outline" onClick={resetEditorState}>
        Cancel
      </Button>
      <Button type="button" onClick={handleSubmit} disabled={!isFormValid}>
        {isEditMode ? 'Save Name' : 'Create Location'}
      </Button>
    </div>
  );

  return (
    <div className="flex flex-col h-full">
      <div className="mb-6 flex items-center justify-between gap-3">
        <h2 className="font-sans text-[24px] font-bold text-text-primary">Saved Locations</h2>
        <Button
          type="button"
          className="h-9 px-5 text-[11px] font-semibold uppercase tracking-[0.12em]"
          onClick={handleCreateNew}
        >
          New Location
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto pr-4 pb-8">
        {locations.length === 0 ? (
          <EmptyState
            title='No locations saved yet.'
            description='Create a location to quickly apply GPS coordinates.'
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {locations.map((location) => (
              <SettingsEntityCard
                key={location.id}
                title={location.name}
                icon={MapPin}
                badge={
                  <Badge variant="secondary" size="sm">
                    Saved
                  </Badge>
                }
                thumbnail={
                  <LocationMapThumbnail
                    latitude={location.latitude}
                    longitude={location.longitude}
                  />
                }
                thumbnailLayout="top"
                thumbnailClassName="h-40"
                metadata={
                  <div className="space-y-1">
                    <p className="font-mono text-[11px]">
                      {location.latitude?.toFixed(7)}, {location.longitude?.toFixed(7)}
                    </p>
                    {location.address ? (
                      <p className="line-clamp-2 text-text-secondary">{location.address}</p>
                    ) : null}
                  </div>
                }
                onEdit={() => handleEdit(location)}
                onDelete={() => handleDeleteRequest(location)}
                editLabel="Edit"
                deleteLabel="Delete"
              />
            ))}
          </div>
        )}
      </div>

      <SettingsEditorDialog
        open={isEditorOpen}
        onOpenChange={(open) => {
          if (!open) {
            resetEditorState();
          }
        }}
        title={isEditMode ? 'Rename Location' : 'Create Location'}
        description={
          isEditMode
            ? 'Update the saved location name. Coordinates and address stay unchanged.'
            : 'Search for a place, refine coordinates, and save it for quick reuse.'
        }
        footer={editorFooter}
      >
        <div className="space-y-5">
          {isEditMode ? (
            <div className="space-y-1.5">
              <Label htmlFor="location-name" className="text-xs text-text-supporting">
                Location name
              </Label>
              <Input
                id="location-name"
                value={formData.name}
                onChange={(event) =>
                  setFormData((previous) => ({ ...previous, name: event.target.value }))
                }
                placeholder="e.g. Home Studio"
              />
              {activeLocation ? (
                <p className="text-[11px] text-text-supporting">
                  Current coordinates: {activeLocation.latitude?.toFixed(7)},{' '}
                  {activeLocation.longitude?.toFixed(7)}
                </p>
              ) : null}
            </div>
          ) : (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="location-search" className="text-xs text-text-supporting">
                  Search place
                </Label>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-text-supporting" />
                  <Input
                    id="location-search"
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    placeholder="Search for an address or place"
                    className="pl-9 pr-9"
                  />
                  {isSearching ? (
                    <Loader2 className="absolute right-3 top-1/2 size-4 -translate-y-1/2 animate-spin text-text-supporting" />
                  ) : null}

                  {searchResults.length > 0 ? (
                    <div className="absolute z-20 mt-1 max-h-52 w-full overflow-y-auto rounded-md border border-border-default bg-bg-elevated shadow-lg">
                      {searchResults.map((result) => (
                        <button
                          key={result.id}
                          type="button"
                          className="w-full border-b border-border-subtle px-3 py-2 text-left last:border-b-0 hover:bg-bg-hover"
                          onClick={() => handleSelectSearchResult(result)}
                        >
                          <p className="text-sm font-medium text-text-primary">
                            {result.primaryName || result.name}
                          </p>
                          {result.secondaryText ? (
                            <p className="text-xs text-text-supporting">{result.secondaryText}</p>
                          ) : null}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
                <p className="text-[11px] text-text-supporting">
                  Enter at least 3 characters to search.
                </p>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="location-name" className="text-xs text-text-supporting">
                    Location name
                  </Label>
                  <Input
                    id="location-name"
                    value={formData.name}
                    onChange={(event) =>
                      setFormData((previous) => ({ ...previous, name: event.target.value }))
                    }
                    placeholder="e.g. Home Studio"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="location-address" className="text-xs text-text-supporting">
                    Address (optional)
                  </Label>
                  <Input
                    id="location-address"
                    value={formData.address}
                    onChange={(event) =>
                      setFormData((previous) => ({ ...previous, address: event.target.value }))
                    }
                    placeholder="Street, city, country"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="location-latitude" className="text-xs text-text-supporting">
                    Latitude
                  </Label>
                  <Input
                    id="location-latitude"
                    type="number"
                    step="0.000001"
                    min="-90"
                    max="90"
                    value={formData.latitude}
                    onChange={(event) =>
                      setFormData((previous) => ({ ...previous, latitude: event.target.value }))
                    }
                    placeholder="40.7589112"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="location-longitude" className="text-xs text-text-supporting">
                    Longitude
                  </Label>
                  <Input
                    id="location-longitude"
                    type="number"
                    step="0.000001"
                    min="-180"
                    max="180"
                    value={formData.longitude}
                    onChange={(event) =>
                      setFormData((previous) => ({ ...previous, longitude: event.target.value }))
                    }
                    placeholder="-73.9331064"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <p className="text-xs text-text-supporting">Map preview</p>
                <div className="relative h-56 overflow-hidden rounded-md border border-border-default/70 bg-bg-tertiary/40">
                  <MapContainer
                    center={mapPosition}
                    zoom={13}
                    style={{ height: '100%', width: '100%' }}
                    zoomControl={false}
                    scrollWheelZoom={false}
                    attributionControl={false}
                    eventHandlers={{ click: handleMapClick }}
                  >
                    <MapViewportController center={mapPosition} />
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    {hasCoordinates ? (
                      <Marker position={[parsedLatitude, parsedLongitude]}>
                        <Popup>
                          <span className="text-xs">{formData.name || 'Selected location'}</span>
                        </Popup>
                      </Marker>
                    ) : null}
                  </MapContainer>
                  <div className="absolute bottom-2 right-2 rounded bg-bg-elevated/90 px-2 py-1 text-[10px] text-text-supporting shadow-sm">
                    Click map to set coordinates
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </SettingsEditorDialog>

      <DeleteConfirmDialog
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteTarget(null);
          }
        }}
        itemLabel={deleteTarget?.name}
        title="Delete location"
        confirmLabel="Delete location"
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
}

function MapViewportController({ center }) {
  const map = useMap();

  useEffect(() => {
    map.setView(center, map.getZoom(), { animate: false });
  }, [center, map]);

  return null;
}

function ReadOnlyMapControls() {
  const map = useMap();

  useEffect(() => {
    map.dragging.disable();
    map.touchZoom.disable();
    map.doubleClickZoom.disable();
    map.scrollWheelZoom.disable();
    map.boxZoom.disable();
    map.keyboard.disable();
    map.zoomControl?.remove();
  }, [map]);

  return null;
}

function LocationMapThumbnail({ latitude, longitude }) {
  return (
    <MapContainer
      center={[latitude, longitude]}
      zoom={13}
      style={{ height: '100%', width: '100%' }}
      zoomControl={false}
      attributionControl={false}
      className="cursor-default"
    >
      <ReadOnlyMapControls />
      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
      <Marker position={[latitude, longitude]} />
    </MapContainer>
  );
}

export default LocationSettings;
